import RadioButtonCheckedRounded from "@mui/icons-material/RadioButtonCheckedRounded";
import RadioButtonUncheckedRounded from "@mui/icons-material/RadioButtonUncheckedRounded";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { KeyboardEvent, ReactNode, Ref } from "react";
import { ALPHA, FONT_SIZE, ICON_SIZE, INK, MOTION, NEUTRAL, SHADOW, SIDE_BY_SIDE_COMPARE, SURFACE } from "../../../theme/tokens";

type Props = {
	title: string;
	subtitle?: ReactNode;
	badge?: ReactNode;
	/** `undefined` = the comparison is read-only and the header is not a control. */
	selected?: boolean;
	onSelect?: () => void;
	onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
	/** Roving tab stop: only the chosen side (or the first, when none is) is in the tab order. */
	tabbable?: boolean;
	buttonRef?: Ref<HTMLButtonElement>;
	dataTestId?: string;
};

/**
 * One side's header card in a `SideBySideCompare`. When the comparison is selectable it is a real
 * `<button role="radio">` — the whole card is the target, with the radio glyph as its affordance.
 * Internal to `SideBySideCompare`; not meant to be used on its own.
 */
export const CompareSideHeader = ({ title, subtitle, badge, selected, onSelect, onKeyDown, tabbable, buttonRef, dataTestId }: Props) => {
	const interactive = selected !== undefined && onSelect !== undefined;
	const Glyph = selected ? RadioButtonCheckedRounded : RadioButtonUncheckedRounded;

	return (
		<Box
			component={interactive ? "button" : "div"}
			type={interactive ? "button" : undefined}
			role={interactive ? "radio" : undefined}
			aria-checked={interactive ? selected : undefined}
			tabIndex={interactive ? (tabbable ? 0 : -1) : undefined}
			ref={buttonRef}
			onClick={onSelect}
			onKeyDown={onKeyDown}
			data-testid={dataTestId}
			data-selected={selected ? "true" : undefined}
			sx={(theme) => ({
				display: "flex",
				// A phone gives each side a third of the screen: the glyph goes above the title so the badge gets the full width.
				flexDirection: { xs: "column", sm: "row" },
				alignItems: "flex-start",
				gap: { xs: 0.75, sm: 1 },
				width: "100%",
				height: "100%",
				boxSizing: "border-box",
				p: { xs: 1.25, sm: 1.5 },
				textAlign: "start",
				fontFamily: "inherit",
				color: "inherit",
				// Top corners only: the card is the head of its lane, which carries on below it.
				borderRadius: `${SIDE_BY_SIDE_COMPARE.LANE_RADIUS_PX}px ${SIDE_BY_SIDE_COMPARE.LANE_RADIUS_PX}px 0 0`,
				border: selected
					? `${SIDE_BY_SIDE_COMPARE.SELECTED_RING_WIDTH}px solid ${theme.palette.primary.main}`
					: `${SIDE_BY_SIDE_COMPARE.SELECTED_RING_WIDTH}px solid ${NEUTRAL[200]}`,
				borderBottomColor: selected ? alpha(theme.palette.primary.main, ALPHA.STATUS_BORDER) : NEUTRAL[200],
				backgroundColor: selected ? alpha(theme.palette.primary.main, SIDE_BY_SIDE_COMPARE.HEADER_WASH_ALPHA) : SURFACE,
				boxShadow: selected ? SHADOW.CONTROL_RAISED : "none",
				transition: theme.transitions.create(["background-color", "border-color", "box-shadow", "transform"], {
					duration: MOTION.DURATION.COLOR_SHIFT,
					easing: MOTION.EASE_OUT,
				}),
				...(interactive && {
					cursor: "pointer",
					"@media (hover: hover) and (pointer: fine)": {
						"&:hover": { backgroundColor: selected ? alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER) : NEUTRAL[50] },
					},
					"&:active": { transform: "scale(0.98)" },
					"&:focus-visible": { outline: `2px solid ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}`, outlineOffset: "2px" },
				}),
				"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
			})}
		>
			{interactive && (
				<Glyph
					aria-hidden
					sx={{
						fontSize: ICON_SIZE.INLINE,
						mt: 0.125,
						flexShrink: 0,
						color: selected ? "primary.main" : INK.DISABLED,
						transition: `color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
					}}
				/>
			)}
			<Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0, flex: 1 }}>
				<Typography component="span" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, lineHeight: 1.3, color: INK.PRIMARY, overflowWrap: "anywhere" }}>
					{title}
				</Typography>
				{subtitle && (
					<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, lineHeight: 1.35 }}>
						{subtitle}
					</Typography>
				)}
				{badge && (
					<Box component="span" sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.25 }}>
						{badge}
					</Box>
				)}
			</Box>
		</Box>
	);
};
